import { Theme } from '@eversports/react-components/design-tokens/theme'
import Checkbox from '@eversports/react-components/primitives/Checkbox'
import ErrorBox from '@eversports/react-components/primitives/ErrorBox'
import Text from '@eversports/react-components/primitives/Text'
import TextField from '@eversports/react-components/primitives/TextField'
import { makeStyles } from '@mui/styles'
import PyroForm, { usePyroField } from 'pyro-form'
import React, { useEffect, useState } from 'react'
import { IbanElement } from '@stripe/react-stripe-js'
import { StripeIbanElementChangeEvent } from '@stripe/stripe-js'

import useCheckoutContext from '../../data/useCheckoutContext'
import { Localized } from '../../localization/react'
import { SepaPaymentMethod } from '../../typings/Payment'

const useStyles = makeStyles((theme: Theme) => ({
  newSepaAccount: {
    border: theme.borders.default,
    borderRadius: theme.borderRadii.default,
    alignSelf: 'flex-start',
  },
  ibanElement: ({ isIbanInputSelected }: { isIbanInputSelected: boolean }) => ({
    border: theme.borders.strong,
    borderColor: isIbanInputSelected ? theme.palette.primary.main : theme.palette.divider,
    borderRadius: theme.borderRadii.default,
    padding: theme.layoutSpacing.default,
    cursor: 'pointer',
  }),
}))

interface InitialValues extends Pick<SepaPaymentMethod, 'bankAccountHolder'> {
  hasUserAgreement: Array<string>
}

const NewBankAccount: React.FC<React.PropsWithChildren<unknown>> = () => {
  const context = useCheckoutContext()

  const [ibanValidationErrorMessage, setIbanValidationErrorMessage] = useState<string | undefined>(undefined)
  const [initialValues, setInitialValues] = useState<InitialValues>()
  const [isIbanInputSelected, setIsIbanInputSelected] = useState(false)
  const [isIbanValid, setIsIbanValid] = useState(false)

  const classes = useStyles({ isIbanInputSelected })
  const { core: bankAccountHolderPyroField } = usePyroField('bankAccountHolder')
  const { core: bankAccountIdPyroField } = usePyroField('pspPaymentMethodId')

  const handleIbanInputFocus = () => {
    setIsIbanInputSelected(true)
  }

  const handleIbanInputBlur = () => {
    setIsIbanInputSelected(false)
  }

  const handleChange = ({ bankAccountHolder, hasUserAgreement }: InitialValues) => {
    if (bankAccountHolder && isIbanValid && Boolean(hasUserAgreement.length)) {
      bankAccountHolderPyroField.onChange(bankAccountHolder)
      context.setIsPaymentValid(true)
    } else if (context.isPaymentValid) {
      context.setIsPaymentValid(false)
    }
  }

  const handleIbanInputChange = (event: StripeIbanElementChangeEvent) => {
    const errorMessage = event.error && event.error.message
    const isIbanInputValid = event.complete

    setIbanValidationErrorMessage(errorMessage)
    setIsIbanValid(isIbanInputValid)
  }

  useEffect(() => {
    bankAccountIdPyroField.onChange('')
    context.setIsPaymentValid(false)

    setInitialValues({
      bankAccountHolder: '',
      hasUserAgreement: [],
    })

    return () => {
      bankAccountHolderPyroField.onChange('')
    }
  }, [])

  if (!initialValues) {
    return null
  }

  return (
    <PyroForm initialValues={initialValues} onChange={handleChange}>
      <TextField
        name="bankAccountHolder"
        label={<Localized id="sepa-bank-account-holder" />}
        fullWidth
        autoFocus
        required
      />
      <label className={classes.ibanElement}>
        <IbanElement
          options={{ supportedCountries: ['SEPA'] }}
          onChange={handleIbanInputChange}
          onFocus={handleIbanInputFocus}
          onBlur={handleIbanInputBlur}
        />
      </label>
      {ibanValidationErrorMessage && <ErrorBox error={{ type: 'message', message: ibanValidationErrorMessage }} />}
      <Text>
        <Localized id="sepa-disclaimer" />
      </Text>
      <Checkbox value="true" name="hasUserAgreement" label={<Localized id="sepa-agreement" />} />
    </PyroForm>
  )
}

export default NewBankAccount
